import {
  TournamentCompetition,
  TournamentCompetitionConflict,
  TournamentCompetitionEvent,
  TournamentEntry,
  TournamentMatch,
  TournamentPlayer,
  TournamentRegistration,
  TournamentSession,
} from "@mixitone/models";
import { List, setupModelRelations } from "@mixitone/oom";
import { debounce, deepEqual, isPresent, withStateToggle } from "@mixitone/util";
import { FormController } from "components/Forms/FormController";
import { messagesService } from "services";
import agentToolsService, { resource } from "services/AgentToolsService";
import routeService from "services/RouteService";
import { TournamentDataAdapter } from "tournament-formats/TournamentDataAdapter";
import {
  buildEntryIdToPlayerIds,
  evaluateAssignedMatchStart,
  formatMatchAssignmentBlockedMessage,
  getCourtOccupiedEntryIds,
  getMatchAssignmentEligibility,
} from "tournament-state/tournamentMatchEligibility";
import { getMatchSlotId } from "tournament-state/TournamentProjection";

interface Props {
  matchList?: string[];
}

interface FormState {
  entries: List<TournamentEntry>;
  allCompetitionEntries: List<TournamentEntry>;
  projectedMatches: List<TournamentMatch>;
  winner: string | null;
  score: Record<any, any>;
  status: TournamentMatch["status"];
  saving: boolean;
  division: number | undefined;
  selectedCompetition?: TournamentCompetition;
  numCourts: number;
  tournamentSession?: TournamentSession;
}

interface FormAttributes {
  tournamentId: string;
  tournament_competition_id?: string;
}

interface OpenTournamentConflict {
  id?: string;
  conflict_key?: string;
  conflict_type?: string;
  status?: string;
  event_ids?: string[];
  payload?: Record<string, unknown>;
}

export class TournamentMatchFormController extends FormController<
  TournamentMatch,
  FormAttributes,
  FormState,
  Props
> {
  /** Serializes saves so concurrent `actionSubmit` calls cannot interleave `appendEvent` writes (division lost from slot payload). */
  private submitTail = Promise.resolve();

  static override initialState = {
    ...FormController.initialState,
    entries: new List(),
    allCompetitionEntries: new List(),
    projectedMatches: new List(),
    winner: null,
    score: {},
    status: "pending" as const,
    saving: false,
    division: undefined,
    numCourts: 0,
  };

  override async buildRecord(): Promise<TournamentMatch> {
    const competitionId =
      this.props.attributes.tournament_competition_id ?? this.props.attributes.tournamentId;
    const match = await this.findProjectedMatch(this.props.id || "", competitionId);
    if (match) return match;
    const fallback = new TournamentMatch({});
    (fallback as any).id = this.props.id;
    return fallback;
  }

  override findModel(id: string) {
    const competitionId =
      this.props.attributes.tournament_competition_id ?? this.props.attributes.tournamentId;
    return this.findProjectedMatch(id, competitionId).then((match) => {
      if (match) return match;
      const fallback = new TournamentMatch({});
      (fallback as any).id = id;
      return fallback;
    });
  }

  private async loadProjectedMatches(competitionId: string) {
    const competition = await TournamentCompetition.query().find(competitionId);
    if (!competition) {
      this.state.projectedMatches = new List();
      return;
    }
    const matches = await new TournamentDataAdapter().loadProjectedMatches(competition);
    this.state.projectedMatches = new List(...matches);
  }

  private async findProjectedMatch(matchId: string, competitionId?: string) {
    if (!matchId || !competitionId) return undefined;
    if (
      this.state.projectedMatches.length === 0 ||
      !this.state.projectedMatches.some((match) => match.tournament_competition_id === competitionId)
    ) {
      await this.loadProjectedMatches(competitionId);
    }
    return this.state.projectedMatches.findById(matchId);
  }

  /**
   * When projection leaves entries.current empty for a completed court match,
   * copy participants from the stored match_completed event so the drawer can
   * render winner buttons. Only runs when there are no entry ids yet (avoids
   * interfering with saves once projection has caught up).
   */
  private async hydrateEntriesFromMatchCompletedEvent(competitionId: string, matchId: string) {
    const recordIds = this.state.record.entries?.current?.filter((id): id is string => !!id) ?? [];
    if (recordIds.length > 0) return;

    const events = await TournamentCompetitionEvent.query()
      .eq("tournament_competition_id", competitionId)
      .all();
    const sorted = [...events].toSorted((a, b) => {
      const ta = a.created_at?.getTime() ?? 0;
      const tb = b.created_at?.getTime() ?? 0;
      return tb - ta;
    });
    for (const ev of sorted) {
      if (ev.event_type !== "match_completed") continue;
      let rawPayload: unknown = ev.payload;
      if (typeof rawPayload === "string") {
        try {
          rawPayload = JSON.parse(rawPayload) as Record<string, unknown>;
        } catch {
          continue;
        }
      }
      if (typeof rawPayload !== "object" || rawPayload === null) continue;
      const p = rawPayload as Record<string, unknown>;
      const slot = p.slot as Record<string, unknown> | undefined;
      if (typeof slot?.slot_id !== "string" || slot.slot_id !== getMatchSlotId({ id: matchId })) continue;
      const pe = p.entries;
      if (!Array.isArray(pe) || typeof pe[0] !== "string" || typeof pe[1] !== "string") continue;
      const base = this.state.record.entries ?? {
        current: [],
        winners: [],
        losers: [],
        score: {},
      };
      this.state.record.entries = {
        ...base,
        current: [pe[0], pe[1]],
        winners: base.winners ?? [],
        losers: base.losers ?? [],
        score: base.score ?? {},
      };
      return;
    }
  }

  private async getSessionAssignmentContext(
    sessionId: string,
    options: { excludeMatchId?: string } = {},
  ): Promise<{
    usedCourts: Set<string>;
    occupiedEntryIds: Set<string>;
    matches: TournamentMatch[];
    entryIdToPlayerIds: Map<string, string[]>;
    standDownMinutes: number;
    uncheckedEntryIds: Set<string>;
  }> {
    const usedCourts = new Set<string>();
    const competitions = await TournamentCompetition.query().eq("tournament_session_id", sessionId).all();
    const adapter = new TournamentDataAdapter();
    const matches: TournamentMatch[] = [];
    for (const sessionCompetition of competitions) {
      matches.push(...(await adapter.loadProjectedMatches(sessionCompetition)));
    }
    const occupiedEntryIds = getCourtOccupiedEntryIds(matches, options);
    for (const match of matches) {
      if (options.excludeMatchId && match.id === options.excludeMatchId) continue;
      if (!(match.isInProgress || match.isReady) || !match.court_id) continue;
      usedCourts.add(String(match.court_id));
    }
    const competitionIds = competitions.map((competition) => competition.id!).filter(isPresent);
    const registrations =
      competitionIds.length > 0
        ? await TournamentRegistration.query().in("tournament_competition_id", competitionIds).all()
        : [];
    const entries =
      competitionIds.length > 0
        ? await TournamentEntry.query().in("tournament_competition_id", competitionIds).all()
        : [];
    const players = await TournamentPlayer.query().eq("tournament_session_id", sessionId).all();
    setupModelRelations({ entries, registrations, players });
    const uncheckedEntryIds = new Set<string>();
    for (const entry of entries) {
      if (!entry.id) continue;
      try {
        if (!entry.isCheckedIn) uncheckedEntryIds.add(entry.id);
      } catch {
        // Relations not loaded.
      }
    }
    const session = await TournamentSession.query().find(sessionId);
    return {
      usedCourts,
      occupiedEntryIds,
      matches,
      entryIdToPlayerIds: buildEntryIdToPlayerIds(registrations),
      standDownMinutes: session?.stand_down_minutes ?? 10,
      uncheckedEntryIds,
    };
  }

  override async onLoad() {
    this.registerAgentResources();
    this.onSubmit = async (_record: TournamentMatch) => {};

    // Get competition ID early for use throughout
    const competitionId =
      this.props.attributes.tournament_competition_id || this.state.record.tournament_competition_id;

    // Always ensure competition is loaded with latest format_data (important after division splits)
    if (competitionId) {
      // Always fetch competition directly from DB to ensure we have latest format_data
      // (important after division splits)
      const competition = await TournamentCompetition.query().find(competitionId);
      if (competition) {
        // Store in state so form can observe it
        this.state.selectedCompetition = competition;
        // Also update the record's competition if it exists
        if (this.state.record.competition) {
          // Update the Loadable with the fresh competition
          await this.state.record.competition.reload();
        }
      }
      await this.loadProjectedMatches(competitionId);
      const matchId = this.props.id || this.state.record.id;
      if (matchId) {
        const fresh = this.state.projectedMatches.findById(matchId);
        const freshIds = fresh?.entries?.current?.filter((id): id is string => !!id) ?? [];
        const recordIds = (this.state.record.entries?.current ?? []).filter((id): id is string => !!id);
        if (fresh && freshIds.length >= 2 && recordIds.length < freshIds.length) {
          this.state.record.entries = fresh.entries;
        }
        await this.hydrateEntriesFromMatchCompletedEvent(competitionId, matchId);
      }
    }

    // Load associated entries
    const currentIds =
      this.state.record.entries?.current?.filter((id): id is string => id !== undefined && id !== "") ?? [];

    if (currentIds.length > 0) {
      const entries = await TournamentEntry.query()
        .in("id", currentIds)
        .preload("registrations", "competition")
        .all();
      const registrations = entries.flatMap((e) => e.registrations);
      const players = await TournamentPlayer.query()
        .in("id", registrations.map((r) => r.tournament_player_id).filter(isPresent))
        .all();
      setupModelRelations({ entries, registrations, players });

      this.state.entries = new List(...entries);

      // Pre-select winner if already set
      if (this.state.record.entries?.winners?.length) {
        this.state.winner = this.state.record.entries.winners[0];
      }
    } else if (this.state.record.entries == null && competitionId) {
      const entries = await TournamentEntry.query().eq("tournament_competition_id", competitionId).all();
      this.state.entries = new List(...entries);
    } else {
      this.state.entries = new List();
    }

    // Always load all competition entries for Custom format entry selection
    if (competitionId) {
      const allEntries = await TournamentEntry.query()
        .eq("tournament_competition_id", competitionId)
        .preload("registrations", "competition")
        .all();
      const allRegistrations = allEntries.flatMap((e) => e.registrations);
      const allPlayers = await TournamentPlayer.query()
        .in("id", allRegistrations.map((r) => r.tournament_player_id).filter(isPresent))
        .all();
      setupModelRelations({ entries: allEntries, registrations: allRegistrations, players: allPlayers });
      this.state.allCompetitionEntries = new List(...allEntries);
    }

    // Set initial status
    this.state.status = this.state.record.status || "pending";
    this.state.score = this.state.record.entries?.score || {};

    // Set initial division from format_data
    this.state.division = this.state.record.format_data?.division as number | undefined;

    // Load tournament session to get number of courts
    const competition = this.state.selectedCompetition;
    if (competition?.tournament_session_id) {
      const session = await TournamentSession.query().find(competition.tournament_session_id);
      if (session) {
        this.state.tournamentSession = session;
        this.state.numCourts = session.courts || 0;
      }
    }
  }

  /**
   * Serialize submissions: multiple rapid updates (division + entries + status + winner) must not run
   * overlapping `saveRecord` — later calls could read stale projection and emit events without `slot.division`.
   */
  override async actionSubmit(): Promise<void> {
    const run = () => super.actionSubmit();
    const next = this.submitTail.then(run);
    this.submitTail = next.catch(() => {});
    await next;
  }

  override async saveRecord(_record: TournamentMatch) {
    const competitionId = this.state.record.tournament_competition_id;
    if (!competitionId || !this.state.record.id) return;
    const competition = await TournamentCompetition.query().find(competitionId);
    if (!competition) return;
    if (
      Array.isArray(competition.format_data?.tournament_conflicts) &&
      competition.format_data.tournament_conflicts.some((conflict: any) => conflict?.status === "open")
    ) {
      throw new Error("Competition is in conflict. Resolve conflicts before editing matches.");
    }

    // Mirror division onto the record so `onLoad` (which resets `this.state.division` from
    // `record.format_data`) cannot drop it between user selection and `appendEvent`.
    if (typeof this.state.division === "number" && Number.isFinite(this.state.division)) {
      this.state.record.format_data = {
        ...(this.state.record.format_data ?? {}),
        division: this.state.division,
      };
    }

    const adapter = new TournamentDataAdapter();
    const persistedMatch = (await adapter.loadProjectedMatches(competition)).find(
      (match) => match.id === this.state.record.id,
    );
    const persistedDivisionRaw = persistedMatch?.format_data?.division;
    const persistedDivision =
      typeof persistedDivisionRaw === "number" && Number.isFinite(persistedDivisionRaw)
        ? persistedDivisionRaw
        : typeof persistedDivisionRaw === "string"
          ? (() => {
              const n = Number.parseInt(persistedDivisionRaw, 10);
              return Number.isNaN(n) ? undefined : n;
            })()
          : undefined;
    const recordDivisionRaw = this.state.record.format_data?.division as number | string | undefined;
    const recordDivision =
      typeof recordDivisionRaw === "number" && Number.isFinite(recordDivisionRaw)
        ? recordDivisionRaw
        : typeof recordDivisionRaw === "string"
          ? (() => {
              const n = Number.parseInt(recordDivisionRaw, 10);
              return Number.isNaN(n) ? undefined : n;
            })()
          : undefined;
    const slotDivision = this.state.division ?? persistedDivision ?? recordDivision;
    const slot = {
      slot_id: getMatchSlotId(this.state.record),
      round: this.state.record.round || 0,
      match_index: this.state.record.match_index || 0,
      division: slotDivision,
    };

    const currentEntries = this.state.entries.map((e) => e?.id).filter((id): id is string => !!id);
    const persistedEntries = (persistedMatch?.entries?.current || []).filter((id): id is string => !!id);
    const entriesChanged = !deepEqual([...persistedEntries].toSorted(), [...currentEntries].toSorted());
    const divisionChanged = (slotDivision ?? undefined) !== (persistedDivision ?? undefined);

    // Emitting match_entries_set on a completed match overwrites slot state and can block
    // winner_selected. Skip when division is unchanged and entries already match projection
    // (including the empty-projection case while the drawer still has both sides).
    const skipEntriesResyncForCompletedCourtMatch =
      this.state.status === "completed" &&
      !divisionChanged &&
      currentEntries.length === 2 &&
      (persistedEntries.length === 0 || !entriesChanged);

    if ((entriesChanged || divisionChanged) && !skipEntriesResyncForCompletedCourtMatch) {
      await adapter.appendEvent({
        competition,
        eventType: "match_entries_set",
        payload: {
          slot,
          entries: currentEntries,
          locked: true,
        },
      });
    }

    if (this.state.status === "in_progress" && !persistedMatch?.isInProgress) {
      await adapter.appendEvent({
        competition,
        eventType: "match_started",
        payload: { slot },
      });
    }

    const persistedWinnerId = persistedMatch?.entries?.winners?.[0];
    if (this.state.status === "completed" && this.state.winner && this.state.winner !== persistedWinnerId) {
      await adapter.appendEvent({
        competition,
        eventType: "winner_selected",
        payload: {
          slot,
          winner_id: this.state.winner,
          score: this.state.score,
          entries: currentEntries,
        },
      });
    } else if (this.state.status === "completed" && !persistedMatch?.isComplete) {
      await adapter.appendEvent({
        competition,
        eventType: "match_completed",
        payload: {
          slot,
          score: this.state.score,
          entries: currentEntries,
        },
      });
    }

    const desiredCourtId = this.state.record.court_id;
    const persistedCourtId = persistedMatch?.court_id;
    if (desiredCourtId !== persistedCourtId) {
      const assignmentContext = await this.getSessionAssignmentContext(competition.tournament_session_id!, {
        excludeMatchId: this.state.record.id,
      });
      const eligibility = getMatchAssignmentEligibility({
        match: this.state.record,
        matches: assignmentContext.matches,
        entryIdToPlayerIds: assignmentContext.entryIdToPlayerIds,
        standDownMinutes: assignmentContext.standDownMinutes,
        now: new Date(),
        occupiedEntryIds: assignmentContext.occupiedEntryIds,
        uncheckedEntryIds: assignmentContext.uncheckedEntryIds,
      });
      if (desiredCourtId && !eligibility.eligible) {
        this.state.record.court_id = undefined;
        messagesService.addMessage(
          formatMatchAssignmentBlockedMessage({
            eligibility,
            standDownMinutes: assignmentContext.standDownMinutes,
          }),
          "warning",
        );
        await adapter.appendEvent({
          competition,
          eventType: "match_unassigned",
          payload: { slot },
        });
      } else {
        if (desiredCourtId) {
          await adapter.appendEvent({
            competition,
            eventType: "match_assigned",
            payload: {
              slot,
              court_id: String(desiredCourtId),
            },
          });
        } else {
          await adapter.appendEvent({
            competition,
            eventType: "match_unassigned",
            payload: { slot },
          });
        }
      }
    }

    window.dispatchEvent(
      new CustomEvent("queueGeneration", {
        detail: { competitionId, immediate: true },
      }),
    );
  }

  private openConflictsForCompetition(competition?: TournamentCompetition): OpenTournamentConflict[] {
    const conflicts = competition?.format_data?.tournament_conflicts;
    if (!Array.isArray(conflicts)) return [];
    return conflicts.filter(
      (conflict): conflict is OpenTournamentConflict =>
        !!conflict && typeof conflict === "object" && (conflict as OpenTournamentConflict).status === "open",
    );
  }

  get openConflictsForCurrentMatch(): OpenTournamentConflict[] {
    const slotId = this.state.record ? getMatchSlotId(this.state.record) : undefined;
    if (!slotId) return [];
    return this.openConflictsForCompetition(this.state.selectedCompetition).filter(
      (conflict) => conflict.payload?.slot_id === slotId,
    );
  }

  get hasOpenConflicts(): boolean {
    return this.openConflictsForCompetition(this.state.selectedCompetition).length > 0;
  }

  @withStateToggle("saving")
  async actionResolveConflict(winnerId: string) {
    if (!this.state.record.id || !this.state.record.tournament_competition_id) return;
    const competition = await TournamentCompetition.query().find(this.state.record.tournament_competition_id);
    if (!competition) return;

    const slotId = getMatchSlotId(this.state.record);
    const persistedConflicts = await TournamentCompetitionConflict.query()
      .eq("tournament_competition_id", competition.id!)
      .all();
    const persistedOpenForSlot = persistedConflicts.filter(
      (conflict) =>
        !conflict.is_deleted && conflict.status === "open" && conflict.payload?.slot_id === slotId,
    );
    const openConflictsForSlot =
      this.openConflictsForCurrentMatch.length > 0 ? this.openConflictsForCurrentMatch : persistedOpenForSlot;
    if (openConflictsForSlot.length === 0 && persistedOpenForSlot.length === 0) {
      messagesService.addMessage("No open conflicts found for this match.", "warning");
      return;
    }

    const currentEntries = this.state.entries.map((e) => e.id).filter((id): id is string => !!id);
    const adapter = new TournamentDataAdapter();
    await adapter.appendEvent({
      competition,
      eventType: "winner_selected",
      payload: {
        slot: {
          slot_id: slotId,
          round: this.state.record.round || 0,
          match_index: this.state.record.match_index || 0,
          division: this.state.division,
        },
        winner_id: winnerId,
        score: this.state.score,
        entries: currentEntries,
        authoritative: true,
      },
    });

    for (const persistedConflict of persistedConflicts) {
      if (persistedConflict.is_deleted || persistedConflict.status === "resolved") continue;
      if (persistedConflict.payload?.slot_id !== slotId) continue;
      persistedConflict.status = "resolved";
      await persistedConflict.save();
    }

    this.state.winner = winnerId;
    this.state.status = "completed";
    this.state.record.status = "completed";
    this.state.selectedCompetition = competition;

    window.dispatchEvent(
      new CustomEvent("queueGeneration", {
        detail: { competitionId: competition.id, immediate: true },
      }),
    );
    messagesService.addMessage("Conflict resolved. Winner selection has been applied.", "success");
  }

  async actionAssignEntry(entry: TournamentEntry, index: number) {
    const currentEntries = [...this.state.entries];
    currentEntries[index] = entry;
    this.state.entries = new List(...currentEntries);
    return this.actionSubmit();
  }

  async actionRemoveEntry(index: number) {
    const currentEntries = [...this.state.entries];
    currentEntries.splice(index, 1);
    this.state.entries = new List(...currentEntries);
    return this.actionSubmit();
  }

  get isCustomFormat(): boolean {
    return this.state.record?.competition?.format === "Custom";
  }

  async actionUpdateStatus(status: TournamentMatch["status"]) {
    this.state.status = status;
    // Keep drawer rendering in sync immediately; many conditions depend on
    // record.isComplete/isInProgress (derived from record.status).
    this.state.record.status = status;
    return this.actionSubmit();
  }

  async actionUpdateWinner(winningEntryId: string) {
    this.state.winner = winningEntryId;
    return this.actionSubmit();
  }

  _debouncedSubmit = debounce(this.actionSubmit.bind(this), 500);
  actionUpdateScore(entryId: string, score: string) {
    this.state.score[entryId] = score;
    this._debouncedSubmit();
  }

  get canDelete(): boolean {
    return this.state.record.isReady || this.state.record.isPending;
  }

  async actionDelete() {
    if (this.canDelete) {
      const competitionId = this.state.record.tournament_competition_id;
      if (competitionId) {
        const competition = await TournamentCompetition.query().find(competitionId);
        if (competition && this.state.record.id) {
          await new TournamentDataAdapter().appendEvent({
            competition,
            eventType: "match_entries_set",
            payload: {
              slot: {
                slot_id: getMatchSlotId(this.state.record),
                round: this.state.record.round || 0,
                match_index: this.state.record.match_index || 0,
              },
              entries: [],
              locked: false,
            },
          });
          window.dispatchEvent(
            new CustomEvent("queueGeneration", {
              detail: { competitionId, immediate: true },
            }),
          );
        }
      }
      messagesService.addMessage("Match deleted", "success");
      this.actionCloseModal();
    } else {
      messagesService.addMessage("Match cannot be deleted", "error");
    }
  }

  get nextOnList(): string | undefined {
    if (!this.props.matchList) return undefined;
    const index = this.props.matchList.indexOf(this.state.record.id!);
    if (index === -1 || index + 1 >= this.props.matchList.length) return undefined;
    return this.props.matchList[index + 1];
  }

  get canVisitNext(): boolean {
    return !!this.nextOnList;
  }

  get hasMatchList(): boolean {
    return Array.isArray(this.props.matchList) && this.props.matchList.length > 0;
  }

  async actionNext() {
    const nextId = this.nextOnList;
    if (!nextId) return;
    const nextMatch = await this.findProjectedMatch(nextId, this.state.record.tournament_competition_id);
    if (!nextMatch) return;

    // Wait for the winner row's `actionSubmit` to finish before unmounting; closing first
    // drops in-flight `saveRecord` work and can skip `winner_selected` (Monrad divisions).
    const deadline = Date.now() + 60_000;
    while (this.state.saving && Date.now() < deadline) {
      await new Promise((resolve) => setTimeout(resolve, 20));
    }

    this.actionCloseModal();
    return routeService.openModal("/tournament/:id/matches/:matchId/edit", {
      params: { id: nextMatch.tournament_competition_id!, matchId: nextMatch.id! },
      state: {
        attributes: {
          tournamentId: nextMatch.tournament_competition_id,
          tournament_competition_id: nextMatch.tournament_competition_id,
        },
        matchList: this.props.matchList,
      },
    });
  }

  async actionAssignCourt() {
    if (!this.state.record.tournament_competition_id || !this.state.record.id) return;
    const competition = await TournamentCompetition.query().find(this.state.record.tournament_competition_id);
    const session = await TournamentSession.query().find(competition!.tournament_session_id!);

    const assignmentContext = session?.id
      ? await this.getSessionAssignmentContext(session.id, { excludeMatchId: this.state.record.id })
      : {
          usedCourts: new Set<string>(),
          occupiedEntryIds: new Set<string>(),
          matches: [],
          entryIdToPlayerIds: new Map<string, string[]>(),
          standDownMinutes: 10,
          uncheckedEntryIds: new Set<string>(),
        };
    const eligibility = getMatchAssignmentEligibility({
      match: this.state.record,
      matches: assignmentContext.matches,
      entryIdToPlayerIds: assignmentContext.entryIdToPlayerIds,
      standDownMinutes: assignmentContext.standDownMinutes,
      now: new Date(),
      occupiedEntryIds: assignmentContext.occupiedEntryIds,
      uncheckedEntryIds: assignmentContext.uncheckedEntryIds,
    });
    if (!eligibility.eligible) {
      messagesService.addMessage(
        formatMatchAssignmentBlockedMessage({
          eligibility,
          standDownMinutes: assignmentContext.standDownMinutes,
        }),
        "warning",
      );
      return;
    }
    const { usedCourts } = assignmentContext;
    const availableCourt = Array.from({ length: session?.courts || 0 }, (_, i) => String(i + 1)).find(
      (court) => !usedCourts.has(court),
    );
    if (!availableCourt) {
      messagesService.addMessage("No available courts to assign", "error");
      return;
    }
    await new TournamentDataAdapter().appendEvent({
      competition: competition!,
      eventType: "match_assigned",
      payload: {
        slot: {
          slot_id: getMatchSlotId(this.state.record),
          round: this.state.record.round || 0,
          match_index: this.state.record.match_index || 0,
          division: this.state.division,
        },
        court_id: availableCourt,
      },
    });
    this.state.record.court_id = availableCourt;
    window.dispatchEvent(
      new CustomEvent("queueGeneration", {
        detail: { competitionId: this.state.record.tournament_competition_id!, immediate: true },
      }),
    );
  }

  async actionStartMatch() {
    if (!this.state.record.tournament_competition_id || !this.state.record.id) return;
    const competition = await TournamentCompetition.query().find(this.state.record.tournament_competition_id);
    if (!competition?.tournament_session_id) return;

    const assignmentContext = await this.getSessionAssignmentContext(competition.tournament_session_id, {
      excludeMatchId: this.state.record.id,
    });
    const start = evaluateAssignedMatchStart({
      match: this.state.record,
      matches: assignmentContext.matches,
      entryIdToPlayerIds: assignmentContext.entryIdToPlayerIds,
      standDownMinutes: assignmentContext.standDownMinutes,
      now: new Date(),
      occupiedEntryIds: assignmentContext.occupiedEntryIds,
      uncheckedEntryIds: assignmentContext.uncheckedEntryIds,
    });
    if (!start.canStart) {
      if (start.message) messagesService.addMessage(start.message, "warning");
      if (start.shouldUnassign && this.state.record.court_id) {
        this.state.record.court_id = undefined;
        await new TournamentDataAdapter().appendEvent({
          competition,
          eventType: "match_unassigned",
          payload: {
            slot: {
              slot_id: getMatchSlotId(this.state.record),
              round: this.state.record.round || 0,
              match_index: this.state.record.match_index || 0,
              division: this.state.division,
            },
          },
        });
        window.dispatchEvent(
          new CustomEvent("queueGeneration", {
            detail: { competitionId: this.state.record.tournament_competition_id!, immediate: true },
          }),
        );
      }
      return;
    }

    this.state.status = "in_progress";
    this.state.record.status = "in_progress";
    await this.actionSubmit();
  }

  async actionFinishMatch() {
    this.state.status = "completed";
    this.state.record.status = "completed";
    await this.actionSubmit();
  }

  async actionUnlockEntries() {
    if (!this.state.record.id || !this.state.record.tournament_competition_id) return;
    const competition = await TournamentCompetition.query().find(this.state.record.tournament_competition_id);
    if (!competition) return;
    await new TournamentDataAdapter().appendEvent({
      competition,
      eventType: "match_entries_set",
      payload: {
        slot: {
          slot_id: getMatchSlotId(this.state.record),
          round: this.state.record.round || 0,
          match_index: this.state.record.match_index || 0,
          division: this.state.division,
        },
        entries: this.state.record.entries?.current || [],
        locked: false,
      },
    });
    window.dispatchEvent(
      new CustomEvent("queueGeneration", {
        detail: { competitionId: this.state.record.tournament_competition_id!, immediate: true },
      }),
    );
  }

  async actionUpdateDivision(division: number | undefined) {
    this.state.division = division;
    const fd = { ...(this.state.record.format_data ?? {}) } as Record<string, unknown>;
    if (typeof division === "number" && Number.isFinite(division)) {
      fd.division = division;
    } else {
      delete fd.division;
    }
    this.state.record.format_data = fd as TournamentMatch["format_data"];
    return this.actionSubmit();
  }

  async actionUpdateCourt(courtId: string | undefined) {
    if (courtId === "") {
      courtId = undefined;
    }
    this.state.record.court_id = courtId;
    return this.actionSubmit();
  }

  protected registerAgentResources() {
    this.addDependency(
      agentToolsService.addResources(this, {
        context: () => {
          return `The user is viewing a form for a tournament match. 
          The match is ${this.state.record.id} with status ${this.state.record.status} 
          and entries ${this.state.record.entries?.current.map((id) => id).join(", ")}.
          The match status is ${this.state.record.status}
          ${this.state.record.entries?.winners?.length ? `The winners are ${this.state.record.entries.winners.map((id) => id).join(", ")}.` : "No winners yet."}
          `;
        },
        definitions: {
          tournament_match: resource("tournament_match", TournamentMatch, {
            list: () => (this.state.record ? new List(this.state.record) : new List()),
          }),
        },
      }),
    );
  }
}
